/**
 * Bulk order box (v4) — local box, one cart write.
 *
 * The box is NOT a live view of the cart any more. It is seeded once, from the
 * cart markup the server already rendered into the page, and after that every
 * +/- is local state only: no fetch, no spinner, no drawer re-render. The cart
 * is written exactly once, when the customer presses "Add to cart", and that one
 * write carries the whole box.
 *
 * Why: the old version wrote to /cart on every tap and re-rendered the drawer
 * behind it, which serialised a request per click, fought the drawer's own
 * writes, and made the section the slowest thing on the page.
 *
 * Subscriptions (Recharge) still work the same way — the app publishes native
 * Shopify selling plans on each variant, the box picks one frequency, and each
 * line is sent with its own `selling_plan` id on the single write.
 */
(function () {
  if (window.customElements.get('bulk-order')) return;

  const ROOT = () => (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/');

  class BulkOrder extends HTMLElement {
    constructor() {
      super();
      // Box contents, keyed by variant id. One entry per product — a variant
      // that sits in the cart twice (one-time and subscribed) is shown once,
      // with the quantities added up.
      this.items = new Map();
      // The cart as it was when the page rendered: [{ key, id, planId, quantity }].
      // Used to work out the smallest set of writes on "Add to cart".
      this.cartLines = [];
      this.discountCodes = [];
      this.seedIndex = new Map();
      this.cartSeed = [];
      this.prefillItems = [];

      this.moneyFormat = this.dataset.moneyformat || '${{amount}}';
      // Order minimums. Both are floors: MOQ counts bars, MOV is the box total
      // in cents at the prices the customer will actually pay.
      this.minQuantity = Math.max(0, parseInt(this.dataset.minQuantity, 10) || 0);
      this.minValue = Math.max(0, parseInt(this.dataset.minValue, 10) || 0);
      this.addLabel = this.dataset.checkoutLabel || 'Add to cart';
      this.discountCode = (this.dataset.discountCode || '').trim();
      // Display-only preview of the discount the code above will apply, so the
      // box can show the same struck-through pair the product cards show.
      this.discountPercent = Math.min(90, Math.max(0, parseFloat(this.dataset.discountPercent) || 0));
      // What the codes already on the cart take off a regular bar, measured
      // from the cart's own lines. A code applied in the drawer wins over the
      // preview above, so the box shows the price the drawer shows.
      this.cartDiscountPercent = 0;
      this.onetimeLabel = this.dataset.onetimeLabel || 'One-time purchase';
      this.shippingNote = '';
      this.busy = false;

      // Subscriptions. `planCache` maps variantId → the selling plans the
      // subscription app allocated to it; `planNames` is the union of plan
      // names across the section, which is what the frequency tiles offer.
      this.subEnabled = false;
      this.purchaseMode = 'onetime';
      this.planCache = new Map();
      this.requiresPlan = new Set();
      this.planNames = [];
      this.planFilters = [];
      this.selectedPlanName = '';
      // Limited editions. The subscription app excludes them and so do the
      // discount codes, so their line always goes in one-time and undiscounted
      // — without taking the subscription away from the rest of the box.
      this.notSubscribable = new Set();
      this.defaultMode = 'onetime';
    }

    connectedCallback() {
      this.progressEl = this.querySelector('[data-progress]');
      this.statusEl = this.querySelector('[data-status]');
      this.countEl = this.querySelector('[data-count]');
      this.subtotalEl = this.querySelector('[data-subtotal]');
      this.totalEl = this.querySelector('[data-total]');
      this.perbarEl = this.querySelector('[data-perbar]');
      this.leNoteEl = this.querySelector('[data-le-note]');
      this.feedbackEl = this.querySelector('[data-bulk-feedback]');
      this.addBtn = this.querySelector('[data-checkout]');
      this.fillBtn = this.querySelector('[data-fill]');
      this.listEl = this.querySelector('[data-bulk-list]');
      this.emptyText = (this.querySelector('[data-bulk-empty]') || {}).textContent || 'Your box is empty.';
      this.listToggle = this.querySelector('[data-list-toggle]');
      this.listCountEl = this.querySelector('[data-list-count]');
      this.boxEl = this.querySelector('.bulk-order__box');
      this.minibar = this.querySelector('[data-minibar]');
      this.miniProgressEl = this.querySelector('[data-mini-progress]');
      this.miniCountEl = this.querySelector('[data-mini-count]');
      this.miniTotalEl = this.querySelector('[data-mini-total]');
      this.miniAddBtn = this.querySelector('[data-mini-checkout]');
      this.subEl = this.querySelector('[data-subscription]');
      this.subInputs = Array.from(this.querySelectorAll('[data-sub-input]'));
      this.subTilesEl = this.querySelector('[data-sub-tiles]');
      this.subBadgeEl = this.querySelector('[data-sub-badge]');
      this.subNoteEl = this.querySelector('[data-sub-note]');
      this.subTitleEl = this.querySelector('[data-sub-subscribe-title]');
      this.subStrikeEl = this.querySelector('[data-sub-strike]');
      this.subPriceSubEl = this.querySelector('[data-sub-price-sub]');
      this.subPriceOneEl = this.querySelector('[data-sub-price-one]');
      this.subStrikeOneEl = this.querySelector('[data-sub-strike-one]');
      this.subInfoToggle = this.querySelector('[data-sub-info-toggle]');
      this.subInfoPanel = this.querySelector('[data-sub-info-panel]');

      // Both halves of the per-bar line come from section settings. The
      // element is absent entirely when the merchant has hidden the line.
      this.perbarTemplate = this.perbarEl ? this.perbarEl.dataset.perbarTemplate || '' : '';
      this.shippingNote = this.perbarEl ? this.perbarEl.dataset.perbarNote || '' : '';
      this.initShippingBar();
      this.readPrefill();
      this.readCart();
      this.initSubscription();
      this.seedItems();

      this.bindGrid();
      this.bindList();
      this.bindAccordion();
      this.bindMinibar();
      if (this.addBtn) this.addBtn.addEventListener('click', () => this.addToCart());
      if (this.fillBtn) this.fillBtn.addEventListener('click', () => this.fillBox());
      this.bindCartWatch();
      this.render();
    }

    /**
     * Discount codes are applied elsewhere — the drawer, the cart page — and
     * the box has to reprice when they are. Only the codes and the line
     * baseline are re-read; the contents of the box stay as the customer left
     * them, since nothing here is written to the cart until Add to cart.
     */
    bindCartWatch() {
      // The theme announces one cart change twice — once on the pub/sub bus and
      // once as a DOM event — so coalesce them into a single read.
      let pending = null;
      const refresh = () => {
        clearTimeout(pending);
        pending = setTimeout(async () => {
          await this.refreshBaseline();
          this.render();
        }, 60);
      };
      // cart-discount.js hands the whole cart back with the event, so no second
      // request is needed on the path that matters most.
      document.addEventListener('discount:update', (event) => {
        const cart = event.detail && event.detail.resource;
        if (cart && Array.isArray(cart.items)) {
          this.adoptCart(cart);
          this.render();
        } else {
          refresh();
        }
      });
      // publish() hands the payload straight to the callback; a DOM event wraps
      // it in `detail`. Either way, the box ignores its own writes.
      const external = (payload) => {
        const detail = payload && payload.detail ? payload.detail : payload;
        if (detail && detail.source === 'bulk-order') return;
        refresh();
      };
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, external);
      }
      document.addEventListener('cart:updated', external);
    }

    // ---- Seeds ---------------------------------------------------------------
    // Both come from JSON the section printed server-side, so the box paints
    // complete on first render with no cart request at all.

    readJSON(selector) {
      const el = this.querySelector(selector);
      if (!el) return [];
      try {
        const parsed = JSON.parse(el.textContent || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }

    // Merchant-chosen products, used only when the cart is empty.
    readPrefill() {
      this.prefillItems = this.readJSON('[data-prefill]')
        .map((it) => this.normaliseSeed(it))
        .filter(Boolean);
    }

    // The cart as rendered by Liquid. Every line is a box row, so the box opens
    // showing exactly what the drawer shows.
    readCart() {
      const el = this.querySelector('[data-cart-lines]');
      this.discountCodes = el && el.dataset.discounts ? el.dataset.discounts.split(',').filter(Boolean) : [];
      const lines = this.readJSON('[data-cart-lines]');
      this.cartDiscountPercent = this.rateFromLines(lines.map((line) => ({
        limitedEdition: !!line.limitedEdition,
        gross: line.originalLinePrice,
        net: line.finalLinePrice,
      })));
      this.cartSeed = lines
        .map((it) => this.normaliseSeed(it))
        .filter(Boolean);
      this.cartLines = this.cartSeed.map((it) => ({
        key: it.key,
        id: it.id,
        planId: it.planId || '',
        quantity: it.quantity,
      }));
    }

    // One shape for both seeds. Plans travel with the seed, so a product that
    // has no card in this grid still prices and subscribes correctly.
    normaliseSeed(raw) {
      if (!raw) return null;
      const id = String(raw.id || '');
      const quantity = parseInt(raw.quantity, 10) || 0;
      if (!id || id === 'null' || quantity < 1) return null;
      if (Array.isArray(raw.sellingPlans) && raw.sellingPlans.length && !this.planCache.has(id)) {
        this.planCache.set(id, raw.sellingPlans);
      }
      if (raw.requiresSellingPlan) this.requiresPlan.add(id);
      if (raw.limitedEdition) this.notSubscribable.add(id);
      return {
        key: raw.key || '',
        id: id,
        title: raw.title || '',
        url: raw.url || '',
        image: raw.image || '',
        price: parseInt(raw.price, 10) || 0,
        quantity: quantity,
        planId: raw.planId ? String(raw.planId) : '',
        planName: raw.planName || '',
      };
    }

    // Fill the box: the cart when it has anything in it, the merchant's
    // pre-filled products when it does not.
    seedItems() {
      const source = this.cartSeed.length ? this.cartSeed : this.prefillItems;
      // Remembered by variant so a row that has no card in the grid can be put
      // back after it is removed.
      this.seedIndex = new Map();
      source.forEach((seed) => { if (!this.seedIndex.has(seed.id)) this.seedIndex.set(seed.id, seed); });
      source.forEach((seed) => {
        const existing = this.items.get(seed.id);
        if (existing) {
          existing.quantity += seed.quantity;
          return;
        }
        this.items.set(seed.id, this.itemFrom(seed.id, seed));
      });

      // Open on the purchase option the cart is already using, so a subscribed
      // cart doesn't silently read back as one-time.
      if (this.subEnabled && this.cartSeed.length) {
        const subscribed = this.cartSeed.find((seed) => seed.planId && seed.planName);
        if (subscribed) {
          const match = this.planNames.find((name) => this.planKey(name) === this.planKey(subscribed.planName));
          this.purchaseMode = 'subscribe';
          this.defaultMode = 'subscribe';
          if (match) this.selectedPlanName = match;
          this.subInputs.forEach((input) => { input.checked = input.value === 'subscribe'; });
          this.syncTiles();
        }
      }
    }

    // Box row for a variant. Card data wins when the product is in this grid —
    // it carries the live price — otherwise the seed supplies the details.
    itemFrom(variantId, seed) {
      const card = this.cardFor(variantId);
      return {
        id: String(variantId),
        title: (card && card.dataset.productTitle) || (seed && seed.title) || '',
        url: (card && card.dataset.productUrl) || (seed && seed.url) || '',
        image: (card && card.dataset.productImage) || (seed && seed.image) || '',
        price: card ? parseInt(card.dataset.price, 10) || 0 : (seed && seed.price) || 0,
        quantity: seed ? seed.quantity : 0,
      };
    }

    cardFor(variantId) {
      return this.querySelector('.bulk-order__card[data-variant-id="' + String(variantId) + '"]');
    }

    // ---- Subscriptions -------------------------------------------------------

    // Selling plans allocated to a variant, read lazily off its card.
    plansFor(variantId) {
      const key = String(variantId);
      if (this.planCache.has(key)) return this.planCache.get(key);
      const card = this.cardFor(key);
      if (!card) return [];
      let plans = [];
      try { plans = JSON.parse(card.dataset.sellingPlans || '[]') || []; } catch (e) { plans = []; }
      if (card.dataset.requiresSellingPlan === 'true') this.requiresPlan.add(key);
      if (card.dataset.limitedEdition === 'true') this.notSubscribable.add(key);
      this.planCache.set(key, plans);
      return plans;
    }

    // A frequency is identified by its plan name, normalised, because the same
    // interval arrives once per product (one selling plan group each) and often
    // twice per product — plain, and again as "… with 20% discount". Stripping a
    // trailing discount clause folds those into one option.
    planKey(name) {
      const normalised = String(name == null ? '' : name).trim().toLowerCase().replace(/\s+/g, ' ');
      const stripped = normalised
        .replace(/[\s,()–—-]*(?:with|save|including|incl\.?)?\s*\d+(?:\.\d+)?\s*%\s*(?:off|discount|savings?)?\s*\)?$/, '')
        .trim();
      return stripped || normalised;
    }

    initSubscription() {
      if (!this.subEl) return;

      this.planFilters = (this.subEl.dataset.planFilter || '')
        .split(',')
        .map((s) => this.planKey(s))
        .filter(Boolean);

      // Key → label. Insertion order is the display order; within a key the
      // shortest name wins, so a "… with 20% discount" duplicate never becomes
      // the tile's caption when the plain name is available.
      const seen = new Map();
      const collect = (plans) => {
        plans.forEach((plan) => {
          if (!plan || !plan.name) return;
          const key = this.planKey(plan.name);
          if (!key || !this.planAllowed(plan.name)) return;
          const label = String(plan.name).trim();
          const current = seen.get(key);
          if (current === undefined || label.length < current.length) seen.set(key, label);
        });
      };
      this.querySelectorAll('.bulk-order__card').forEach((card) => collect(this.plansFor(card.dataset.variantId)));
      this.planCache.forEach(collect);

      const names = Array.from(seen.values());
      // No selling plans anywhere in this section — drop the whole control.
      if (!names.length) {
        this.subEl.remove();
        this.subEl = null;
        return;
      }

      this.subEnabled = true;
      this.planNames = names;
      this.selectedPlanName = names[0];
      this.buildTiles();

      const checked = this.subInputs.find((input) => input.checked);
      this.purchaseMode = checked && checked.value === 'subscribe' ? 'subscribe' : 'onetime';
      this.defaultMode = this.purchaseMode;
      this.subInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (!input.checked || input.disabled) return;
          this.purchaseMode = input.value === 'subscribe' ? 'subscribe' : 'onetime';
          this.defaultMode = this.purchaseMode;
          this.setFeedback('');
          this.render();
        });
      });

      if (this.subInfoToggle && this.subInfoPanel) {
        this.subInfoToggle.addEventListener('click', (event) => {
          event.preventDefault();
          const open = this.subInfoPanel.hidden;
          this.subInfoPanel.hidden = !open;
          this.subInfoToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
      }

      this.subEl.hidden = false;
    }

    // One tile per frequency. Real radios keep arrow-key navigation working.
    buildTiles() {
      if (!this.subTilesEl) return;
      const frag = document.createDocumentFragment();
      this.planNames.forEach((name, index) => {
        const tile = document.createElement('label');
        tile.className = 'bulk-order__sub-tile';
        tile.dataset.subTile = name;

        const input = document.createElement('input');
        input.type = 'radio';
        input.className = 'bulk-order__sub-tile-radio';
        input.name = 'bulk-frequency-' + (this.dataset.sectionId || 'x');
        input.value = name;
        input.checked = index === 0;
        input.addEventListener('change', () => {
          if (!input.checked) return;
          this.selectedPlanName = name;
          this.setFeedback('');
          this.render();
        });

        const label = document.createElement('span');
        label.className = 'bulk-order__sub-tile-name';
        label.textContent = name;

        const save = document.createElement('span');
        save.className = 'bulk-order__sub-tile-save';
        save.dataset.subTileSave = name;

        tile.appendChild(input);
        tile.appendChild(label);
        tile.appendChild(save);
        frag.appendChild(tile);
      });
      this.subTilesEl.innerHTML = '';
      this.subTilesEl.appendChild(frag);
    }

    syncTiles() {
      if (!this.subTilesEl) return;
      this.subTilesEl.querySelectorAll('.bulk-order__sub-tile').forEach((tile) => {
        const selected = this.planKey(tile.dataset.subTile) === this.planKey(this.selectedPlanName);
        const input = tile.querySelector('input');
        if (input) input.checked = selected;
      });
    }

    // Frequencies the merchant allows, matched loosely so "8 week" selects
    // "8 Week Subscription". No filter set means every frequency is offered.
    planAllowed(name) {
      if (!this.planFilters.length) return true;
      const key = this.planKey(name);
      return this.planFilters.some((f) => key === f || key.indexOf(f) !== -1);
    }

    // The plan for one frequency inside a variant's allocations. When a
    // frequency resolves to more than one plan — the usual "plain" and "with
    // 20% discount" pair — take the cheapest, so the customer is never quietly
    // charged the undiscounted rate for the frequency they picked.
    findPlan(plans, name) {
      const want = this.planKey(name);
      if (!want) return null;
      let best = null;
      for (const plan of plans) {
        if (!plan || this.planKey(plan.name) !== want) continue;
        if (!best || (typeof plan.price === 'number' && plan.price < best.price)) best = plan;
      }
      return best;
    }

    // The plan for a variant under the box's current purchase option, or null
    // when that product isn't sold on this frequency (mixed boxes are allowed).
    planFor(variantId) {
      if (!this.subEnabled || this.purchaseMode !== 'subscribe' || !this.selectedPlanName) return null;
      return this.findPlan(this.plansFor(variantId), this.selectedPlanName);
    }

    // What one bar costs under the current purchase option.
    unitPrice(item) {
      const plan = this.planFor(item.id);
      return plan && typeof plan.price === 'number' ? plan.price : item.price;
    }

    // Saving for one frequency, as a whole percent of the same box bought
    // one-time. Measured over the box when it has contents, otherwise over the
    // section's products so the tiles still read correctly when empty.
    planDiscount(planName) {
      let base = 0;
      let sub = 0;
      const consider = (variantId, price, quantity) => {
        const plan = this.findPlan(this.plansFor(variantId), planName);
        if (!plan || typeof plan.price !== 'number' || !price) return;
        base += price * quantity;
        sub += plan.price * quantity;
      };
      if (this.items.size) {
        this.items.forEach((item) => consider(item.id, item.price, item.quantity));
      } else {
        this.querySelectorAll('.bulk-order__card').forEach((card) => {
          consider(card.dataset.variantId, parseInt(card.dataset.price, 10) || 0, 1);
        });
      }
      if (!base || sub >= base) return 0;
      return Math.round(((base - sub) / base) * 100);
    }

    maxSubscriptionDiscount() {
      let best = 0;
      this.planNames.forEach((name) => { best = Math.max(best, this.planDiscount(name)); });
      return best;
    }

    /**
     * Fill the placeholders any of this section's copy can carry:
     *
     *   {maxDiscount} / {discount}  the largest subscription saving, from the
     *                               plans the app publishes
     *   {codeDiscount}              the discount code's percent — the cart's own
     *                               code when one is applied, otherwise the
     *                               section's "Discount to preview" percent
     *   {code}                      the section's discount code
     *
     * Then tidy the spacing left behind by a placeholder that resolved to
     * nothing.
     */
    applyDiscountVars(text, discount) {
      const value = discount > 0 ? discount + '%' : '';
      return String(text || '')
        .replace(/\{maxDiscount\}/g, value)
        .replace(/\{discount\}/g, value)
        .replace(/\{codeDiscount\}/g, this.discountPercentLabel())
        .replace(/\{code\}/g, this.discountCode)
        .replace(/\s+/g, ' ')
        .trim();
    }

    /**
     * Whether copy with placeholders has everything it needs to read properly.
     * "Save up to {maxDiscount} with code {code}" is worse than useless when
     * either value is missing, so the badge stays hidden instead.
     */
    placeholdersResolve(text) {
      if (!text) return false;
      if (/\{(maxDiscount|discount)\}/.test(text) && this.maxSubscriptionDiscount() <= 0) return false;
      if (/\{codeDiscount\}/.test(text) && !this.activeDiscountPercent()) return false;
      if (/\{code\}/.test(text) && !this.discountCode) return false;
      return true;
    }

    // "Orange Blossom and Lemon Pop", or "A, B and 2 more" past a couple.
    nameList(items) {
      const names = items.map((i) => i.title).filter(Boolean);
      if (!names.length) return items.length + (items.length === 1 ? ' item' : ' items');
      if (names.length > 2) return names.slice(0, 2).join(', ') + ' and ' + (names.length - 2) + ' more';
      return names.join(' and ');
    }

    // Items the app does not sell on the chosen frequency — they stay one-time.
    itemsWithoutPlan() {
      const out = [];
      this.items.forEach((item) => { if (!this.planFor(item.id)) out.push(item); });
      return out;
    }

    // A limited edition, by the same rule the badge uses. The subscription app
    // excludes them and so do the discount codes, so this answers both "can it
    // be subscribed to" and "does the code touch it".
    isLimitedEdition(variantId) {
      const key = String(variantId);
      // plansFor() populates the Set from the card, so it has to run first.
      this.plansFor(key);
      return this.notSubscribable.has(key);
    }

    /**
     * Keep the radios showing the option the box is actually on.
     *
     * Nothing in the box can take the subscription away: a mixed box is a
     * normal cart, and Shopify carries a selling plan per line. A product the
     * app publishes no plans for — a limited edition always, since the app
     * excludes them — simply goes in one-time alongside the subscribed bars,
     * and its row and the note underneath say so.
     *
     * Runs first in render(), because unit prices depend on the mode.
     */
    enforcePurchaseMode() {
      if (!this.subEnabled) return;
      this.subInputs.forEach((input) => {
        input.checked = input.value === this.purchaseMode;
      });
    }

    // Subscription-only variants (Shopify's `requires_selling_plan`) cannot be
    // bought one-time; adding them without a plan is rejected by Shopify.
    itemsNeedingPlan() {
      const out = [];
      this.items.forEach((item) => {
        if (this.requiresPlan.has(String(item.id)) && !this.planFor(item.id)) out.push(item);
      });
      return out;
    }

    renderSubscription(subtotalOnetime, subtotalSubscribe) {
      if (!this.subEnabled || !this.subEl) return;

      // Checked state and the disabled/locked state are both settled by
      // enforcePurchaseMode, which ran first; this only paints the selection.
      this.subInputs.forEach((input) => {
        const option = input.closest('.bulk-order__sub-option');
        if (option) option.classList.toggle('is-selected', input.checked);
      });

      const maxDiscount = this.maxSubscriptionDiscount();

      if (this.subBadgeEl) {
        const template = this.subBadgeEl.dataset.template || this.subBadgeEl.textContent || '';
        if (this.placeholdersResolve(template)) {
          this.subBadgeEl.dataset.template = template;
          this.subBadgeEl.textContent = this.applyDiscountVars(template, maxDiscount);
          this.subBadgeEl.hidden = false;
        } else {
          this.subBadgeEl.hidden = true;
        }
      }

      if (this.subTitleEl) {
        const template = this.subTitleEl.dataset.template || this.subTitleEl.textContent || '';
        this.subTitleEl.dataset.template = template;
        this.subTitleEl.textContent = this.applyDiscountVars(template, maxDiscount);
      }

      // Benefit / drawback copy may carry the same placeholders. Keep the
      // merchant's original markup around so re-rendering stays lossless.
      [this.querySelector('[data-sub-benefits]'), this.querySelector('[data-sub-drawbacks]')]
        .filter(Boolean)
        .forEach((el) => {
          const template = el.dataset.template || el.innerHTML;
          el.dataset.template = template;
          el.innerHTML = this.applyDiscountVars(template, maxDiscount);
        });

      if (this.subTilesEl) {
        this.subTilesEl.querySelectorAll('.bulk-order__sub-tile').forEach((tile) => {
          const name = tile.dataset.subTile;
          const selected = this.planKey(name) === this.planKey(this.selectedPlanName);
          tile.classList.toggle('is-selected', selected);
          const input = tile.querySelector('input');
          if (input) input.checked = selected;
          const save = tile.querySelector('[data-sub-tile-save]');
          if (save) {
            const discount = this.planDiscount(name);
            save.textContent = discount > 0 ? 'save ' + discount + '%' : '';
          }
        });
      }

      // Both tiles quote what the customer would actually pay, so a discount
      // code on the cart moves them the way it moves the drawer. The code only
      // bites on the regular bars, so the saving is measured per item at that
      // tile's price rather than taken off its total.
      const netOnetime = Math.max(0, subtotalOnetime - this.discountAmountCents((item) => item.price));
      const netSubscribe = Math.max(0, subtotalSubscribe - this.discountAmountCents((item) => {
        const plan = this.findPlan(this.plansFor(item.id), this.selectedPlanName);
        return plan && typeof plan.price === 'number' ? plan.price : item.price;
      }));

      if (this.subStrikeEl) {
        const show = this.items.size > 0 && netSubscribe < netOnetime;
        this.subStrikeEl.textContent = show ? this.formatMoney(netOnetime) : '';
        this.subStrikeEl.hidden = !show;
      }
      if (this.subPriceSubEl) {
        this.subPriceSubEl.textContent = this.items.size ? this.formatMoney(netSubscribe) : '';
      }
      // The one-time tile shows the regular price struck through only when a
      // code is taking something off it.
      if (this.subStrikeOneEl) {
        const show = this.items.size > 0 && netOnetime < subtotalOnetime;
        this.subStrikeOneEl.textContent = show ? this.formatMoney(subtotalOnetime) : '';
        this.subStrikeOneEl.hidden = !show;
      }
      if (this.subPriceOneEl) {
        this.subPriceOneEl.textContent = this.items.size ? this.formatMoney(netOnetime) : '';
      }

      if (this.subNoteEl) {
        let note = '';
        if (this.purchaseMode === 'subscribe') {
          const missing = this.itemsWithoutPlan();
          if (missing.length) {
            // A limited edition is never on subscription at all; anything else
            // here is simply not sold on the frequency that is selected.
            const reason = missing.every((item) => this.isLimitedEdition(item.id))
              ? ' not available as a subscription, so '
              : ' not sold on this frequency, so ';
            note = this.nameList(missing) +
              (missing.length === 1 ? ' is' : ' are') + reason +
              (missing.length === 1 ? 'it stays' : 'they stay') +
              ' a one-time purchase.';
          }
        } else {
          const forced = this.itemsNeedingPlan();
          if (forced.length) {
            note = forced.length + (forced.length === 1 ? ' item is' : ' items are') +
              ' subscription-only — choose a subscription to check out.';
          }
        }
        this.subNoteEl.textContent = note;
      }
    }

    // ---- Order minimums ------------------------------------------------------

    minimumsGap(count, total) {
      return {
        quantity: Math.max(0, this.minQuantity - count),
        value: Math.max(0, this.minValue - total),
      };
    }

    minimumsGapText(gap) {
      const parts = [];
      if (gap.quantity > 0) parts.push(gap.quantity + ' more bar' + (gap.quantity === 1 ? '' : 's'));
      if (gap.value > 0) parts.push(this.formatMoney(gap.value) + ' more');
      return parts.join(' and ');
    }

    hasMinimums() {
      return this.minQuantity > 0 || this.minValue > 0;
    }

    minimumsRequirementText() {
      const parts = [];
      if (this.minQuantity > 0) parts.push(this.minQuantity + ' bar' + (this.minQuantity === 1 ? '' : 's'));
      if (this.minValue > 0) parts.push(this.formatMoney(this.minValue));
      return parts.join(' and ');
    }

    // With both minimums set the least-complete one wins, so the bar only
    // reaches 100% when the box is genuinely ready.
    minimumsProgress(count, total) {
      if (!this.hasMinimums()) return count > 0 ? 100 : 0;
      const ratios = [];
      if (this.minQuantity > 0) ratios.push(count / this.minQuantity);
      if (this.minValue > 0) ratios.push(total / this.minValue);
      return Math.min(100, Math.max(0, Math.min.apply(null, ratios) * 100));
    }

    // ---- Local box edits -----------------------------------------------------
    // Nothing here talks to the cart. The customer can tap as fast as they like.

    setQuantity(variantId, quantity) {
      const key = String(variantId);
      const next = Math.max(0, parseInt(quantity, 10) || 0);
      if (next === 0) {
        this.items.delete(key);
      } else {
        const item = this.items.get(key);
        if (item) {
          item.quantity = next;
        } else {
          // Not in the box: either a card in the grid, or a product that was
          // seeded from the cart and then removed — the seed is kept so taking
          // a row out is not a dead end.
          const seed = this.seedIndex.get(key);
          const card = this.cardFor(key);
          if (!seed && (!card || card.dataset.available !== 'true')) return;
          const fresh = this.itemFrom(key, seed);
          fresh.quantity = next;
          this.items.set(key, fresh);
        }
      }
      this.setFeedback('');
      this.render();
    }

    step(variantId, delta) {
      const item = this.items.get(String(variantId));
      this.setQuantity(variantId, (item ? item.quantity : 0) + delta);
    }

    totalCount() {
      let n = 0;
      this.items.forEach((i) => { n += i.quantity; });
      return n;
    }

    // Subtotal at the prices the customer will actually pay.
    subtotalCents() {
      let s = 0;
      this.items.forEach((i) => { s += this.unitPrice(i) * i.quantity; });
      return s;
    }

    // Subtotal ignoring the purchase option, for the one-time price shown next
    // to the radios.
    onetimeSubtotalCents() {
      let s = 0;
      this.items.forEach((i) => { s += i.price * i.quantity; });
      return s;
    }

    // "Fill with bestsellers": top the box up until it clears the order
    // minimums, spreading bars across the highest-rated in-stock products first.
    fillBox() {
      const targetMet = (count, subtotal) => {
        const gap = this.minimumsGap(count, subtotal);
        return gap.quantity <= 0 && gap.value <= 0;
      };

      let count = this.totalCount();
      let subtotal = this.subtotalCents();
      if (targetMet(count, subtotal)) { this.setFeedback(''); return; }

      const cards = Array.from(this.querySelectorAll('.bulk-order__card'))
        .filter((card) => card.dataset.available === 'true')
        .sort((a, b) => (parseInt(b.dataset.rating, 10) || 0) - (parseInt(a.dataset.rating, 10) || 0));
      if (!cards.length) return;

      const additions = new Map();
      let guard = 0;
      const cap = 5000; // hard stop so an unreachable target can't spin forever
      while (!targetMet(count, subtotal) && guard < cap) {
        for (const card of cards) {
          if (targetMet(count, subtotal)) break;
          const variantId = card.dataset.variantId;
          const plan = this.planFor(variantId);
          const price = plan && typeof plan.price === 'number' ? plan.price : parseInt(card.dataset.price, 10) || 0;
          additions.set(variantId, (additions.get(variantId) || 0) + 1);
          count += 1;
          subtotal += price;
          guard += 1;
          if (guard >= cap) break;
        }
      }
      if (!additions.size) return;

      additions.forEach((quantity, variantId) => {
        const item = this.items.get(String(variantId));
        const fresh = item || this.itemFrom(variantId, null);
        fresh.quantity = (item ? item.quantity : 0) + quantity;
        this.items.set(String(variantId), fresh);
      });
      this.setFeedback('');
      this.render();
    }

    formatMoney(cents) {
      const value = (cents / 100).toFixed(2);
      return this.moneyFormat.replace(/\{\{\s*amount[^}]*\}\}/g, value) || '$' + value;
    }

    /**
     * The share a set of cart lines lost to discount codes, as a percent.
     * Limited editions are excluded from the codes, so they are left out of the
     * measurement as well as out of the saving — otherwise a box holding one
     * would read the code as weaker than it is.
     */
    rateFromLines(lines) {
      let gross = 0;
      let net = 0;
      lines.forEach((line) => {
        if (!line || line.limitedEdition) return;
        const full = parseInt(line.gross, 10) || 0;
        if (full <= 0) return;
        gross += full;
        net += parseInt(line.net, 10) || 0;
      });
      if (gross <= 0 || net >= gross) return 0;
      return Math.min(90, ((gross - net) / gross) * 100);
    }

    // What comes off a regular bar. A code on the cart wins over the section's
    // preview percent: once one is applied the box has a real number to show,
    // and it should be the one the drawer is showing.
    activeDiscountPercent() {
      return this.cartDiscountPercent > 0 ? this.cartDiscountPercent : this.discountPercent;
    }

    // The same number as words, for the {codeDiscount} placeholder.
    discountPercentLabel() {
      const percent = Math.round(this.activeDiscountPercent());
      return percent > 0 ? percent + '%' : '';
    }

    /**
     * The saving the discount takes off the box, in cents. Limited editions are
     * excluded from the codes, so the percent is charged against the regular
     * bars only — which is why this is measured over the items rather than
     * taken off the total. `priceOf` picks the price to measure; it defaults to
     * what the current purchase option charges.
     */
    discountAmountCents(priceOf) {
      const percent = this.activeDiscountPercent();
      if (!percent) return 0;
      const price = priceOf || ((item) => this.unitPrice(item));
      let base = 0;
      this.items.forEach((item) => {
        if (this.isLimitedEdition(item.id)) return;
        base += price(item) * item.quantity;
      });
      return Math.round((base * percent) / 100);
    }

    // What the customer pays for the box once the discount is applied.
    discountedCents(cents) {
      return Math.max(0, cents - this.discountAmountCents());
    }

    /**
     * A price for display: just the amount, or the regular amount struck
     * through followed by the discounted one. Returns HTML, so the money string
     * is escaped — a shop's money format can carry markup of its own.
     */
    priceHTML(cents) {
      const net = this.discountedCents(cents);
      if (net === cents) return this.escapeHtml(this.formatMoney(cents));
      return (
        '<s class="bulk-order__was">' + this.escapeHtml(this.formatMoney(cents)) + '</s>' +
        this.escapeHtml(this.formatMoney(net))
      );
    }

    // ---- Free shipping bar ---------------------------------------------------

    initShippingBar() {
      const el = this.querySelector('[data-shipping-bar]');
      if (!el) return;
      this.shippingSource = el.dataset.shippingSource || 'box';
      this.shippingThreshold = parseInt(el.dataset.shippingThreshold, 10) || 0;
      this.shippingCartCents = parseInt(el.dataset.shippingCart, 10) || 0;
      this.shippingProgressTemplate = el.dataset.shippingProgress || '';
      this.shippingCompleteText = el.dataset.shippingComplete || '';
      this.shippingTextEl = el.querySelector('.free-shipping-bar p');
      this.shippingTrackEl = el.querySelector('.free-shipping-bar__track');
      this.shippingProgressBarEl = el.querySelector('progress-bar');
    }

    /**
     * `boxSubtotal` is what the customer actually pays — the discounted total,
     * not the struck-through regular one. Free shipping is earned on the amount
     * charged, so a box whose regular price clears the threshold but whose
     * discounted price does not must still read as short.
     */
    renderShipping(boxSubtotal) {
      if (!this.shippingThreshold) return;

      // The box now holds the cart's own lines, so "box + cart" would count the
      // same bars twice — the box total is the cart total once it is submitted.
      // `cart.total_price` is already net of discounts, matching the box side.
      let amount = boxSubtotal;
      if (this.shippingSource === 'cart') amount = this.shippingCartCents;

      const remaining = Math.max(0, this.shippingThreshold - amount);
      const complete = remaining <= 0;
      const pct = Math.max(0, Math.min(100, (amount / this.shippingThreshold) * 100));

      if (this.shippingTextEl) {
        this.shippingTextEl.textContent = complete
          ? this.shippingCompleteText
          : this.shippingProgressTemplate.replace('__AMOUNT__', this.formatMoney(remaining));
      }
      if (this.shippingTrackEl) {
        // The truck reads --progress-bar-width off the track it sits on.
        this.shippingTrackEl.style.setProperty('--progress-bar-width', pct + '%');
        this.shippingTrackEl.classList.toggle('free-shipping-bar__track--complete', complete);
        this.shippingTrackEl.classList.toggle('free-shipping-bar__track--in-progress', !complete);
      }
      if (this.shippingProgressBarEl) {
        // ProgressBar (global.js) writes the same custom property onto itself
        // 300ms after it first becomes visible, from these two data attributes.
        this.shippingProgressBarEl.dataset.quantity = amount;
        this.shippingProgressBarEl.dataset.total = this.shippingThreshold;
        this.shippingProgressBarEl.style.setProperty('--progress-bar-width', pct + '%');
        this.shippingProgressBarEl.classList.toggle('progress-bar--complete', complete);
        this.shippingProgressBarEl.classList.toggle('progress-bar--in-progress', !complete);
      }
    }

    // ---- Render --------------------------------------------------------------

    render() {
      // Settle the purchase option first — unit prices depend on it.
      this.enforcePurchaseMode();
      const count = this.totalCount();
      const total = this.subtotalCents();

      if (this.subEnabled) {
        let subscribeSubtotal = 0;
        this.items.forEach((i) => {
          const plan = this.findPlan(this.plansFor(i.id), this.selectedPlanName);
          const planPrice = plan && typeof plan.price === 'number' ? plan.price : i.price;
          subscribeSubtotal += planPrice * i.quantity;
        });
        this.renderSubscription(this.onetimeSubtotalCents(), subscribeSubtotal);
      }

      const gap = this.minimumsGap(count, total);
      const belowMinimum = gap.quantity > 0 || gap.value > 0;

      const pct = this.minimumsProgress(count, total);
      if (this.progressEl) this.progressEl.style.width = pct + '%';
      const bar = this.progressEl ? this.progressEl.closest('[role="progressbar"]') : null;
      if (bar) bar.setAttribute('aria-valuenow', count);

      if (this.statusEl) {
        let status;
        if (!this.hasMinimums()) {
          status = count + (count === 1 ? ' bar' : ' bars') + ' in your box';
        } else if (belowMinimum) {
          status = count === 0
            ? 'Orders start at ' + this.minimumsRequirementText() + '.'
            : 'Add ' + this.minimumsGapText(gap) + ' to check out.';
        } else {
          status = count + (count === 1 ? ' bar' : ' bars') + ' in your box — ready to check out.';
        }
        this.statusEl.textContent = status;
        this.statusEl.classList.toggle('is-full', !belowMinimum && count > 0);
      }

      this.renderShipping(this.discountedCents(total));

      if (this.countEl) this.countEl.textContent = count;
      if (this.listCountEl) this.listCountEl.textContent = this.items.size;
      if (this.subtotalEl) this.subtotalEl.innerHTML = this.priceHTML(total);
      if (this.totalEl) this.totalEl.innerHTML = this.priceHTML(total);

      // Per-bar restates the price on show, so it divides the discounted total.
      this.renderPerBar(count, this.discountedCents(total));
      this.renderLimitedNote();

      // Add to cart — locked until the order minimums are met. Same state drives
      // the mobile mini-bar button.
      const blocked = belowMinimum || count < 1;
      let label;
      if (belowMinimum && count > 0) label = this.escapeHtml('Add ' + this.minimumsGapText(gap));
      else if (count < 1) label = this.escapeHtml(this.addLabel);
      else label = this.escapeHtml(this.addLabel + ' · ') + this.priceHTML(total);
      this.setButtons(blocked, label);

      if (this.miniProgressEl) this.miniProgressEl.style.width = pct + '%';
      const miniBar = this.miniProgressEl ? this.miniProgressEl.closest('[role="progressbar"]') : null;
      if (miniBar) miniBar.setAttribute('aria-valuenow', count);
      if (this.miniCountEl) this.miniCountEl.textContent = count;
      if (this.miniTotalEl) this.miniTotalEl.innerHTML = this.priceHTML(total);

      this.renderList();
      this.syncCards();
    }

    // `label` is HTML — the price half carries the struck regular price. Callers
    // passing plain words escape them first.
    setButtons(disabled, label) {
      [this.addBtn, this.miniAddBtn].forEach((btn) => {
        if (!btn) return;
        btn.disabled = disabled || this.busy;
        btn.innerHTML = label;
      });
    }

    // "Just $10.00 a bar · Free shipping included". The price half is dropped
    // while the box is empty — there is nothing to divide by — leaving the note
    // on its own, and either half can be blank, in which case the middot goes
    // with it.
    renderPerBar(count, total) {
      if (!this.perbarEl) return;
      const parts = [];
      if (this.perbarTemplate && count > 0) {
        parts.push(this.perbarTemplate.replace(/\{price\}/g, this.formatMoney(Math.round(total / count))));
      }
      if (this.shippingNote) parts.push(this.shippingNote);
      this.perbarEl.textContent = parts.join(' · ');
    }

    // "Discount won't apply for limited edition product", under the per-bar note.
    // Printed by Liquid and hidden; it appears while the box holds at least one
    // limited edition and goes away with the last of them, so the line is only
    // ever on screen when it is true of something the customer is buying.
    renderLimitedNote() {
      if (!this.leNoteEl) return;
      let limited = false;
      this.items.forEach((item) => {
        if (!limited && this.isLimitedEdition(item.id)) limited = true;
      });
      this.leNoteEl.hidden = !limited;
    }

    // The box list. Rows are keyed by variant, so one product is one row.
    renderList() {
      if (!this.listEl) return;
      if (this.items.size === 0) {
        this.listEl.innerHTML = '<li class="bulk-order__items-empty" data-bulk-empty>' + this.escapeHtml(this.emptyText) + '</li>';
        return;
      }
      this.listEl.innerHTML = Array.from(this.items.values()).map((item) => {
        const id = this.escapeAttr(item.id);
        const img = item.image
          ? '<img class="bulk-order__item-image" src="' + this.escapeAttr(item.image) + '" alt="" loading="lazy" width="48" height="48">'
          : '<span class="bulk-order__item-image"></span>';
        const name = item.url
          ? '<a href="' + this.escapeAttr(item.url) + '" class="bulk-order__item-name">' + this.escapeHtml(item.title) + '</a>'
          : '<span class="bulk-order__item-name">' + this.escapeHtml(item.title) + '</span>';
        // Limited editions are excluded from the subscription app, so they can
        // only ever be bought once. Saying so on the row explains why the box
        // locked itself to a one-time purchase.
        const note = this.isLimitedEdition(item.id)
          ? '<span class="bulk-order__item-note">' + this.escapeHtml(this.onetimeLabel) + '</span>'
          : '';
        return '' +
          '<li class="bulk-order__item" data-variant="' + id + '">' +
            img +
            '<div class="bulk-order__item-info">' +
              name +
              note +
              '<div class="bulk-order__item-meta">' +
                '<span class="bulk-order__qty" data-qty-control>' +
                  '<button type="button" class="bulk-order__qty-btn" data-item-decrement="' + id + '" aria-label="Decrease quantity">&minus;</button>' +
                  '<input type="number" class="bulk-order__qty-input" data-item-qty="' + id + '" value="' + item.quantity + '" min="0" step="1" inputmode="numeric" aria-label="Quantity">' +
                  '<button type="button" class="bulk-order__qty-btn" data-item-increment="' + id + '" aria-label="Increase quantity">+</button>' +
                '</span>' +
              '</div>' +
            '</div>' +
            '<button type="button" class="bulk-order__item-remove" data-item-remove="' + id + '" aria-label="Remove">' +
              '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M2 2L12 12M12 2L2 12"/></svg>' +
            '</button>' +
          '</li>';
      }).join('');
    }

    escapeHtml(str) {
      return String(str == null ? '' : str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }

    // Attributes need the quotes escaped too — a product title with a quote in
    // it would otherwise break out of the attribute.
    escapeAttr(str) {
      return this.escapeHtml(str).replace(/"/g, '&quot;');
    }

    // Mirror each card's stepper with the box quantity.
    syncCards() {
      this.querySelectorAll('.bulk-order__card').forEach((card) => {
        const item = this.items.get(String(card.dataset.variantId));
        const quantity = item ? item.quantity : 0;
        const qtyInput = card.querySelector('[data-qty-input]');
        card.classList.toggle('is-in-list', quantity > 0);
        if (qtyInput && qtyInput.value !== String(quantity)) qtyInput.value = quantity;
      });
    }

    setFeedback(msg, isError) {
      if (!this.feedbackEl) return;
      this.feedbackEl.textContent = msg || '';
      this.feedbackEl.classList.toggle('bulk-order__feedback--error', !!isError);
    }

    // ---- Bindings ------------------------------------------------------------

    bindGrid() {
      // One listener for the whole grid instead of four per card.
      const grid = this.querySelector('.bulk-order__grid');
      if (!grid) return;

      grid.addEventListener('click', (event) => {
        const card = event.target.closest('.bulk-order__card');
        if (!card) return;
        const variantId = card.dataset.variantId;
        if (event.target.closest('[data-bulk-add]')) {
          if (card.dataset.available !== 'true') return;
          this.step(variantId, 1);
        } else if (event.target.closest('[data-qty-increment]')) {
          this.step(variantId, 1);
        } else if (event.target.closest('[data-qty-decrement]')) {
          this.step(variantId, -1);
        }
      });

      grid.addEventListener('change', (event) => {
        const input = event.target.closest('[data-qty-input]');
        if (!input) return;
        const card = input.closest('.bulk-order__card');
        if (card) this.setQuantity(card.dataset.variantId, input.value);
      });
    }

    // The box list is re-rendered on every change, so use delegation.
    bindList() {
      if (!this.listEl) return;
      this.listEl.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-item-remove]');
        if (remove) { this.setQuantity(remove.dataset.itemRemove, 0); return; }
        const inc = event.target.closest('[data-item-increment]');
        if (inc) { this.step(inc.dataset.itemIncrement, 1); return; }
        const dec = event.target.closest('[data-item-decrement]');
        if (dec) this.step(dec.dataset.itemDecrement, -1);
      });
      this.listEl.addEventListener('change', (event) => {
        const input = event.target.closest('[data-item-qty]');
        if (input) this.setQuantity(input.dataset.itemQty, input.value);
      });
    }

    // Item list is an accordion — collapsed by default on mobile only.
    bindAccordion() {
      if (!this.listToggle || !this.boxEl) return;
      this.setListCollapsed(window.matchMedia('(max-width: 749px)').matches);
      this.listToggle.addEventListener('click', () => {
        this.setListCollapsed(!this.boxEl.classList.contains('is-list-collapsed'));
      });
    }

    setListCollapsed(collapsed) {
      if (!this.boxEl || !this.listToggle) return;
      this.boxEl.classList.toggle('is-list-collapsed', collapsed);
      this.listToggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    }

    // The mini-bar fades in once the full panel is scrolled above the viewport
    // (mobile only). Uses scroll position so it works with Lenis smooth scroll.
    bindMinibar() {
      if (this.miniAddBtn) this.miniAddBtn.addEventListener('click', () => this.addToCart());
      if (!this.minibar || !this.boxEl) return;

      const mq = window.matchMedia('(max-width: 749px)');
      let ticking = false;
      const update = () => {
        ticking = false;
        if (!mq.matches) { this.minibar.classList.remove('is-visible'); return; }
        // "Crossed the panel" — its bottom has passed the top of the viewport.
        const show = this.boxEl.getBoundingClientRect().bottom < 80;
        this.minibar.classList.toggle('is-visible', show);
        this.minibar.setAttribute('aria-hidden', show ? 'false' : 'true');
      };
      const onScroll = () => {
        if (!ticking) { ticking = true; requestAnimationFrame(update); }
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      if (window.lenis && typeof window.lenis.on === 'function') window.lenis.on('scroll', onScroll);
      update();
    }

    // ---- The one cart write --------------------------------------------------

    async postJSON(url, body) {
      const res = await fetch(ROOT() + url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || 'Could not update your cart.');
      return data;
    }

    /**
     * Turn the box into cart operations.
     *
     * `updates` sets absolute quantities on lines the cart already has, keyed by
     * line key — which keeps each line's existing selling plan — and zeroes the
     * lines the box no longer wants. `adds` covers what isn't in the cart yet, or
     * is there on the wrong plan, each carrying its own `selling_plan`.
     */
    buildCartOps() {
      const updates = {};
      const adds = [];
      const handled = new Set();

      this.items.forEach((item) => {
        const plan = this.planFor(item.id);
        const wantPlan = plan && plan.id ? String(plan.id) : '';
        const lines = this.cartLines.filter((line) => line.id === item.id);
        const match = lines.find((line) => (line.planId || '') === wantPlan);
        if (match) {
          updates[match.key] = item.quantity;
          handled.add(match.key);
        } else {
          adds.push(
            wantPlan
              ? { id: parseInt(item.id, 10), quantity: item.quantity, selling_plan: parseInt(wantPlan, 10) }
              : { id: parseInt(item.id, 10), quantity: item.quantity }
          );
        }
      });

      // Anything the cart holds that the box no longer wants — including the
      // same variant sitting on the other purchase option — goes to zero.
      this.cartLines.forEach((line) => {
        if (!handled.has(line.key)) updates[line.key] = 0;
      });

      return { updates: updates, adds: adds };
    }

    async addToCart() {
      if (this.busy || this.items.size === 0) return;

      const count = this.totalCount();
      const total = this.subtotalCents();
      const gap = this.minimumsGap(count, total);
      if (gap.quantity > 0 || gap.value > 0) {
        this.setFeedback(
          'Orders start at ' + this.minimumsRequirementText() + '. Add ' + this.minimumsGapText(gap) + ' to check out.',
          true
        );
        return;
      }

      // Subscription-only variants must carry a selling plan or Shopify rejects
      // the line with "Variant can only be purchased with a selling plan".
      if (this.itemsNeedingPlan().length) {
        this.setFeedback(
          this.purchaseMode === 'subscribe'
            ? 'Some items are not sold on this delivery frequency. Pick another frequency to continue.'
            : 'Some items in your box are subscription-only. Choose the subscription option to continue.',
          true
        );
        return;
      }

      this.busy = true;
      this.setFeedback('');
      this.setButtons(true, 'Adding…');

      // The box does not follow the cart while the customer edits it, so take
      // one look at the real cart here — before writing, not on every tap — so
      // the line keys are current even if the drawer changed something first.
      await this.refreshBaseline();

      const ops = this.buildCartOps();
      const hasUpdates = Object.keys(ops.updates).length > 0;
      if (!hasUpdates && !ops.adds.length) {
        this.busy = false;
        this.render();
        return;
      }

      // Only the last write asks Shopify to re-render the drawer, so the drawer
      // markup is built once per press instead of once per line.
      const sections = this.cartSections();
      const sectionsBody = sections.length
        ? { sections: sections, sections_url: window.location.pathname }
        : {};

      try {
        // Two writes at most: one to set the quantities of lines the cart
        // already has (and to apply the section's discount code), one to add
        // what is new. Only the last of them re-renders the drawer.
        let parsed = null;
        if (hasUpdates || this.discountCode) {
          const body = {};
          if (hasUpdates) body.updates = ops.updates;
          if (this.discountCode) body.discount = this.discountPayload();
          if (!ops.adds.length) Object.assign(body, sectionsBody);
          parsed = await this.postJSON('cart/update.js', body);
        }
        if (ops.adds.length) {
          parsed = await this.postJSON('cart/add.js', Object.assign({ items: ops.adds }, sectionsBody));
        }

        this.applySections(parsed);
        // /cart/update.js answers with the whole cart, so when it was the last
        // write the new line keys come back for free. /cart/add.js answers with
        // the added lines only — no read-back for it, the refresh at the top of
        // the next press covers that.
        if (parsed && typeof parsed.item_count === 'number') this.adoptCart(parsed);
        this.busy = false;
        this.render();
        this.flashAdded();
      } catch (error) {
        this.busy = false;
        this.setFeedback(error.message || 'Could not update your cart.', true);
        this.render();
      }
    }

    // Existing cart discounts plus this section's code — update.js replaces the
    // whole list, so the ones already on the cart have to be sent again.
    discountPayload() {
      const codes = this.discountCodes.slice();
      if (this.discountCode && codes.indexOf(this.discountCode) === -1) codes.push(this.discountCode);
      return codes.join(',');
    }

    // Sections the theme's drawer knows how to swap — the drawer itself plus the
    // header cart bubbles. Empty when there is no drawer on the page.
    cartSections() {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        return drawer.getSectionsToRender().map((section) => section.id);
      }
      return [];
    }

    // Swap the freshly rendered cart markup in and let the drawer open itself if
    // the merchant has "open cart after adding" turned on, then tell the rest of
    // the theme the cart moved.
    applySections(parsed) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && parsed && parsed.sections && typeof drawer.renderContents === 'function') {
        drawer.renderContents(parsed);
      }
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bulk-order' });
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'bulk-order' } }));
    }

    // The cart as it stands, so the write below addresses lines that really
    // exist. Never touches what is displayed — only the line keys behind it.
    async refreshBaseline() {
      try {
        const res = await fetch(ROOT() + 'cart.js', { cache: 'no-store', headers: { Accept: 'application/json' } });
        this.adoptCart(await res.json());
      } catch (e) {
        // Keep the old baseline; the write still resolves against real keys for
        // the lines it already knows about.
      }
    }

    adoptCart(cart) {
      if (!cart || !Array.isArray(cart.items)) return;
      this.cartLines = cart.items.map((line) => ({
        key: line.key,
        id: String(line.variant_id || line.id),
        planId: line.selling_plan_allocation ? String(line.selling_plan_allocation.selling_plan.id) : '',
        quantity: line.quantity,
      }));
      this.discountCodes = (cart.discount_codes || [])
        .filter((d) => d && d.code && d.applicable !== false)
        .map((d) => d.code);
      this.cartDiscountPercent = this.rateFromLines(cart.items.map((line) => ({
        limitedEdition: this.isLimitedEdition(line.variant_id || line.id),
        gross: line.original_line_price,
        net: line.final_line_price,
      })));
      this.shippingCartCents = cart.total_price || 0;
    }

    flashAdded() {
      if (!this.addBtn) return;
      [this.addBtn, this.miniAddBtn].forEach((btn) => {
        if (btn) btn.textContent = 'Added to cart ✓';
      });
      clearTimeout(this.flashTimer);
      // Re-rendered rather than restored from a saved string: the label carries
      // markup now (the struck regular price), and render() rebuilds it from
      // the box as it stands, which is also correct if anything changed.
      this.flashTimer = setTimeout(() => this.render(), 1600);
    }
  }

  window.customElements.define('bulk-order', BulkOrder);
})();
